import { z } from 'zod';
import { DocId, IsoTimestamp, TenantId } from '../common.js';
/**
 * Eén notificatie voor één gebruiker.
 *
 * Firestore path: tenants/{tenantId}/users/{userId}/notifications/{id}
 *
 * Fan-out (een document per ontvanger) en niet afleiden-bij-lezen: het paneel
 * is daarmee één query ongeacht hoeveel soorten er bijkomen, terwijl afleiden
 * met elke nieuwe soort een extra bronquery per paneelopening kost.
 *
 * De notificatie is geen archief. Het origineel staat altijd in de bron en
 * `link` wijst ernaar; daarom ruimt een Firestore TTL-policy op `expiresAt`
 * ze na 90 dagen op in plaats van een janitor-job.
 */
/**
 * Gecureerde enum conform CLAUDE.md. Bevat alleen soorten die daadwerkelijk
 * gevuurd worden: stap 1 was de bel plus het incidentpad, daarna kwam de DM
 * erbij. De rest (aanmeldingen, wachtrijen, Sara, intervisie) volgt nog.
 */
export const NotificationType = z.enum([
    'incident_nieuw',
    'incident_termijn',
    'dm_bericht',
    'kanaal_bericht',
    'declaratie_nieuw',
    'declaratie_beoordeeld',
    'ai_tools_aanvraag',
    'ai_tools_aangezet',
    'agenda_aanmelding',
    'changelog_nieuw',
]);
export const NotificationSchema = z.object({
    id: DocId,
    tenantId: TenantId,
    userId: DocId,
    type: NotificationType,
    /**
     * Volledige regel zoals hij in het paneel staat. Bewust voorgekookt bij het
     * schrijven en niet bij het lezen samengesteld: de tekst van een
     * incidentmelding mag nooit de omschrijving of de naam van de melder
     * bevatten, en die regel bewaak je op één plek (de bron) in plaats van in
     * elke render.
     */
    title: z.string().min(1).max(200),
    /** Pad binnen de app, bijvoorbeeld `/beheer/incidenten`. Nooit extern. */
    link: z.string().min(1).max(500),
    /**
     * Aantal samengevouwen gebeurtenissen. Afwezig of 1 bij een losse melding.
     * Samenvouwen gebeurt via een deterministische document-id (zie
     * NotificationService), zodat een tweede aanmelding voor hetzelfde evenement
     * dit veld ophoogt in plaats van een tweede regel te maken.
     */
    count: z.number().int().min(1).optional(),
    createdAt: IsoTimestamp,
    /** Bijgewerkt bij elke samenvouwing; het paneel sorteert hierop. */
    updatedAt: IsoTimestamp,
    /** Afwezig = ongelezen. Een samenvouwing wist dit veld weer. */
    readAt: IsoTimestamp.optional(),
    /** Invoer voor de Firestore TTL-policy: createdAt + 90 dagen. */
    expiresAt: IsoTimestamp,
});
export const ListNotificationsResponse = z.object({
    notifications: z.array(NotificationSchema),
    unread: z.number().int().min(0),
});
/** Bewaartermijn in dagen. Eén getal, hier zodat backend en docs niet uiteenlopen. */
export const NOTIFICATION_TTL_DAYS = 90;
/**
 * Per-gebruiker schakelaars voor de meldingsstrook en de tabtitel-teller.
 * De bel toont altijd alles; dit stuurt alleen wat er "plopt". Kanaal staat
 * standaard uit: een druk kanaal zou anders de hele praktijk om de minuut een
 * strook geven (besluit Jos 3 sep 2026). Opgeslagen op het User-document.
 */
export const MeldingVoorkeuren = z.object({
    dm: z.boolean().default(true),
    kanaal: z.boolean().default(false),
    overig: z.boolean().default(true),
});
export const MELDING_VOORKEUREN_STANDAARD = MeldingVoorkeuren.parse({});
/** Welke schakelaar een soort melding bestuurt. Alles buiten berichten is "overig". */
export function meldingCategorie(type) {
    if (type === 'dm_bericht')
        return 'dm';
    if (type === 'kanaal_bericht')
        return 'kanaal';
    return 'overig';
}
//# sourceMappingURL=notification.js.map